import { logger } from '../logger';
import type { Model, ModelSettings } from '../model';
import type { AgentInputItem } from '../types';
import { Usage } from '../usage';
import { formatItemsForSummary } from './prompt';
import { buildStableHistoryItems } from './refs';
import { estimateTextTokens, estimateTokensForItems } from './token-estimator';
import type {
  SessionMemoryBackgroundUpdateConfig,
  SessionMemoryConfig,
  SessionMemorySnapshot,
  SessionMemoryStore,
} from './types';

const DEFAULT_MINIMUM_TOKENS_TO_INIT = 10_000;
const DEFAULT_MINIMUM_TOKENS_BETWEEN_UPDATE = 5_000;
const DEFAULT_WAIT_FOR_UPDATE_MS = 15_000;
const DEFAULT_MAX_MEMORY_TOKENS = 20_000;

export const SESSION_MEMORY_HEADINGS = [
  'Session Title',
  'Current State',
  'Task specification',
  'Files and Functions',
  'Workflow',
  'Errors & Corrections',
  'Codebase and System Documentation',
  'Learnings',
  'Key results',
  'Worklog',
] as const;

export type ResolvedSessionMemoryBackgroundUpdateConfig = {
  minimumTokensToInit: number;
  minimumTokensBetweenUpdate: number;
  waitForUpdateMs: number;
  maxMemoryTokens: number;
  model?: string | Model;
  modelSettings?: ModelSettings;
};

export type SessionMemoryUpdateResult = {
  snapshot: SessionMemorySnapshot | null;
  usage: Usage;
  skipped: boolean;
  reason?: string;
};

export function resolveSessionMemoryBackgroundUpdateConfig(
  config: false | SessionMemoryConfig | undefined,
): ResolvedSessionMemoryBackgroundUpdateConfig | undefined {
  if (!config) {
    return undefined;
  }
  if (isSessionMemoryStore(config)) {
    return withSessionMemoryUpdateDefaults({});
  }
  if (config.backgroundUpdate === false) {
    return undefined;
  }
  return withSessionMemoryUpdateDefaults(config.backgroundUpdate ?? {});
}

export async function updateSessionMemory(options: {
  model: Model;
  store: SessionMemoryStore;
  sessionId: string;
  items: AgentInputItem[];
  config: ResolvedSessionMemoryBackgroundUpdateConfig;
  signal?: AbortSignal;
}): Promise<SessionMemoryUpdateResult> {
  const tokenCountAtUpdate = estimateTokensForItems(options.items);
  const existing = await options.store.read(options.sessionId);
  const updateDecision = shouldUpdateSessionMemory(
    existing,
    tokenCountAtUpdate,
    options.config,
  );
  if (!updateDecision.update) {
    return {
      snapshot: existing,
      usage: new Usage(),
      skipped: true,
      reason: updateDecision.reason,
    };
  }

  const lastSummarizedItemId = findLastSummarizableItemId(options.items);
  if (!lastSummarizedItemId) {
    return {
      snapshot: existing,
      usage: new Usage(),
      skipped: true,
      reason: 'no_summarizable_items',
    };
  }

  const prompt = buildSessionMemoryUpdatePrompt({
    currentMemory: isValidSessionMemoryContent(existing?.content)
      ? existing!.content
      : createEmptySessionMemory(),
    items: options.items,
    maxMemoryTokens: options.config.maxMemoryTokens,
  });
  const { reasoning, ...modelSettings } = options.config.modelSettings ?? {};
  const response = await options.model.getResponse({
    systemInstructions:
      'You update long-lived session memory. Return the complete memory.md text only and never request tool calls.',
    input: [
      {
        type: 'message',
        role: 'user',
        content: prompt,
      },
    ],
    modelSettings: {
      toolChoice: 'none',
      maxTokens: options.config.maxMemoryTokens,
      reasoning: {
        effort: 'none',
        ...(reasoning ?? {}),
      },
      ...modelSettings,
    },
    tools: [],
    toolsExplicitlyProvided: true,
    handoffs: [],
    outputType: 'text',
    tracing: 'enabled_without_data',
    signal: options.signal,
  });

  const content = extractModelText(response).trim();
  if (!content) {
    throw new Error('Session memory updater returned no text output.');
  }
  assertValidSessionMemoryContent(content);
  if (estimateTextTokens(content) > options.config.maxMemoryTokens) {
    throw new Error(
      `Session memory updater returned ${estimateTextTokens(content)} estimated tokens, exceeding maxMemoryTokens=${options.config.maxMemoryTokens}.`,
    );
  }

  const snapshot: SessionMemorySnapshot = {
    content,
    lastSummarizedItemId,
    updatedAt: new Date().toISOString(),
    tokenCountAtUpdate,
  };
  await options.store.write(options.sessionId, snapshot);
  return {
    snapshot,
    usage: response.usage ?? new Usage(),
    skipped: false,
  };
}

export function buildSessionMemoryUpdatePrompt(options: {
  currentMemory: string;
  items: AgentInputItem[];
  maxMemoryTokens: number;
}): string {
  return [
    'Update the session memory from the conversation below.',
    'Return the full memory.md document only. Do not wrap it in code fences. Do not call tools.',
    `Keep the result under ${options.maxMemoryTokens} estimated tokens.`,
    '',
    'Rules:',
    '- Preserve every top-level heading from the current memory exactly once and in the same order.',
    '- Replace placeholder instructions with concrete facts when facts are known.',
    '- Keep information that future turns need: user intent, current state, files, commands, outputs, errors, corrections, decisions, and pending tasks.',
    '- Remove stale or superseded details when the conversation corrects them.',
    '- Do not include private chain-of-thought or reasoning traces.',
    '- If a section has no durable facts yet, leave its italic instruction in place.',
    '',
    '<current_memory>',
    options.currentMemory.trim(),
    '</current_memory>',
    '',
    'Conversation to incorporate:',
    formatItemsForSummary(options.items),
  ].join('\n');
}

export function createEmptySessionMemory(): string {
  return [
    '# Session Title',
    '_A short and distinctive 5-10 word descriptive title for the session. Super info dense, no filler_',
    '',
    '# Current State',
    '_What is actively being worked on right now? Pending tasks not yet completed. Immediate next steps._',
    '',
    '# Task specification',
    '_What did the user ask to build? Any design decisions or other explanatory context_',
    '',
    '# Files and Functions',
    '_What are the important files? In short, what do they contain and why are they relevant?_',
    '',
    '# Workflow',
    '_What bash commands are usually run and in what order? How to interpret their output if not obvious?_',
    '',
    '# Errors & Corrections',
    '_Errors encountered and how they were fixed. What did the user correct? What approaches failed and should not be tried again?_',
    '',
    '# Codebase and System Documentation',
    '_What are the important system components? How do they work/fit together?_',
    '',
    '# Learnings',
    '_What has worked well? What has not? What to avoid? Do not duplicate items from other sections_',
    '',
    '# Key results',
    '_If the user asked a specific output such as an answer to a question, a table, or other document, repeat the exact result here_',
    '',
    '# Worklog',
    '_Step by step, what was attempted, done? Very terse summary for each step_',
  ].join('\n');
}

export function isValidSessionMemoryContent(
  content: string | undefined,
): content is string {
  if (!content?.trim()) {
    return false;
  }
  try {
    assertValidSessionMemoryContent(content);
    return true;
  } catch {
    return false;
  }
}

export function assertValidSessionMemoryContent(content: string): void {
  let cursor = -1;
  for (const heading of SESSION_MEMORY_HEADINGS) {
    const index = findHeadingIndex(content, heading);
    if (index < 0) {
      throw new Error(
        `Session memory is missing required heading: # ${heading}`,
      );
    }
    if (index <= cursor) {
      throw new Error(`Session memory heading is out of order: # ${heading}`);
    }
    cursor = index;
  }
}

export class SessionMemoryUpdateQueue {
  readonly #states = new Map<
    string,
    {
      pending?: () => Promise<void>;
      inFlight?: Promise<void>;
      waiters: Set<() => void>;
    }
  >();

  schedule(options: { sessionId: string; run: () => Promise<void> }): void {
    const state = this.#getState(options.sessionId);
    state.pending = options.run;
    if (!state.inFlight) {
      this.#startNext(options.sessionId, state);
    }
  }

  async waitForIdle(sessionId: string, timeoutMs: number): Promise<void> {
    const state = this.#states.get(sessionId);
    if (!state?.inFlight && !state?.pending) {
      return;
    }
    if (timeoutMs <= 0) {
      return;
    }
    await Promise.race([
      new Promise<void>((resolve) => {
        this.#getState(sessionId).waiters.add(resolve);
      }),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  async flush(): Promise<void> {
    while (true) {
      const active = [...this.#states.values()]
        .map((state) => state.inFlight)
        .filter((promise): promise is Promise<void> => Boolean(promise));
      const hasPending = [...this.#states.values()].some((state) =>
        Boolean(state.pending),
      );
      if (active.length === 0 && !hasPending) {
        return;
      }
      if (active.length === 0) {
        await Promise.resolve();
        continue;
      }
      await Promise.allSettled(active);
    }
  }

  #getState(sessionId: string) {
    let state = this.#states.get(sessionId);
    if (!state) {
      state = { waiters: new Set<() => void>() };
      this.#states.set(sessionId, state);
    }
    return state;
  }

  #startNext(
    sessionId: string,
    state: {
      pending?: () => Promise<void>;
      inFlight?: Promise<void>;
      waiters: Set<() => void>;
    },
  ): void {
    const job = state.pending;
    if (!job) {
      this.#notifyIdle(sessionId, state);
      return;
    }
    state.pending = undefined;
    let promise!: Promise<void>;
    promise = (async () => {
      try {
        await job();
      } catch (error) {
        logger.warn(
          'Session memory background update failed:',
          error instanceof Error ? error.message : String(error),
        );
      } finally {
        if (state.inFlight === promise) {
          state.inFlight = undefined;
        }
        if (state.pending) {
          this.#startNext(sessionId, state);
        } else {
          this.#notifyIdle(sessionId, state);
        }
      }
    })();
    state.inFlight = promise;
  }

  #notifyIdle(
    sessionId: string,
    state: {
      pending?: () => Promise<void>;
      inFlight?: Promise<void>;
      waiters: Set<() => void>;
    },
  ): void {
    if (state.inFlight || state.pending) {
      return;
    }
    const waiters = [...state.waiters];
    state.waiters.clear();
    for (const waiter of waiters) {
      waiter();
    }
    if (state.waiters.size === 0) {
      this.#states.delete(sessionId);
    }
  }
}

function withSessionMemoryUpdateDefaults(
  config: Exclude<SessionMemoryBackgroundUpdateConfig, false>,
): ResolvedSessionMemoryBackgroundUpdateConfig {
  return {
    minimumTokensToInit: Math.max(
      0,
      Math.floor(config.minimumTokensToInit ?? DEFAULT_MINIMUM_TOKENS_TO_INIT),
    ),
    minimumTokensBetweenUpdate: Math.max(
      0,
      Math.floor(
        config.minimumTokensBetweenUpdate ??
          DEFAULT_MINIMUM_TOKENS_BETWEEN_UPDATE,
      ),
    ),
    waitForUpdateMs: Math.max(
      0,
      Math.floor(config.waitForUpdateMs ?? DEFAULT_WAIT_FOR_UPDATE_MS),
    ),
    maxMemoryTokens: Math.max(
      1,
      Math.floor(config.maxMemoryTokens ?? DEFAULT_MAX_MEMORY_TOKENS),
    ),
    ...(config.model !== undefined ? { model: config.model } : {}),
    ...(config.modelSettings !== undefined
      ? { modelSettings: config.modelSettings }
      : {}),
  };
}

function shouldUpdateSessionMemory(
  snapshot: SessionMemorySnapshot | null,
  currentTokenCount: number,
  config: ResolvedSessionMemoryBackgroundUpdateConfig,
): { update: boolean; reason?: string } {
  if (!snapshot?.content || !snapshot.lastSummarizedItemId) {
    return currentTokenCount >= config.minimumTokensToInit
      ? { update: true }
      : { update: false, reason: 'below_minimum_tokens_to_init' };
  }
  if (!isValidSessionMemoryContent(snapshot.content)) {
    return currentTokenCount >= config.minimumTokensToInit
      ? { update: true }
      : { update: false, reason: 'invalid_memory_below_init_threshold' };
  }
  if (typeof snapshot.tokenCountAtUpdate !== 'number') {
    return currentTokenCount >= config.minimumTokensToInit
      ? { update: true }
      : { update: false, reason: 'missing_token_watermark' };
  }
  const delta = currentTokenCount - snapshot.tokenCountAtUpdate;
  if (delta < config.minimumTokensBetweenUpdate) {
    return { update: false, reason: 'below_minimum_tokens_between_update' };
  }
  return { update: true };
}

function findLastSummarizableItemId(
  items: AgentInputItem[],
): string | undefined {
  return [...buildStableHistoryItems(items)]
    .reverse()
    .find(({ item }) => !isSystemMessage(item) && !isReasoningItem(item))?.id;
}

function extractModelText(response: { output: unknown[] }): string {
  return response.output
    .flatMap((item) => {
      if (!item || typeof item !== 'object') {
        return [];
      }
      const record = item as Record<string, unknown>;
      if (record.type !== 'message') {
        return [];
      }
      const content = record.content;
      if (typeof content === 'string') {
        return [content];
      }
      if (!Array.isArray(content)) {
        return [];
      }
      return content.flatMap((part) => {
        if (!part || typeof part !== 'object') {
          return [];
        }
        const partRecord = part as Record<string, unknown>;
        return partRecord.type === 'output_text' &&
          typeof partRecord.text === 'string'
          ? [partRecord.text]
          : [];
      });
    })
    .join('');
}

function findHeadingIndex(content: string, heading: string): number {
  const pattern = new RegExp(
    `(?:^|\\n)# ${escapeRegExp(heading)}(?:\\n|$)`,
    'u',
  );
  const match = pattern.exec(content);
  return match?.index ?? -1;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isSessionMemoryStore(
  value: SessionMemoryConfig,
): value is SessionMemoryStore {
  const maybeStore = value as Partial<SessionMemoryStore>;
  return (
    typeof maybeStore.read === 'function' &&
    typeof maybeStore.write === 'function'
  );
}

function isSystemMessage(item: AgentInputItem): boolean {
  return (
    item &&
    typeof item === 'object' &&
    item.type === 'message' &&
    item.role === 'system'
  );
}

function isReasoningItem(item: AgentInputItem): boolean {
  return Boolean(
    item &&
      typeof item === 'object' &&
      (item as { type?: unknown }).type === 'reasoning',
  );
}
